<template>
  <main class="support-page mx-auto w-full max-w-6xl px-5 py-8 sm:px-7 lg:px-9">
    <header class="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm">
      <div class="relative isolate px-6 py-8 sm:px-9 sm:py-10">
        <div class="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_90%_10%,rgba(45,140,255,0.16),transparent_34%),radial-gradient(circle_at_75%_100%,rgba(125,86,255,0.12),transparent_36%)]"></div>
        <div class="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div class="max-w-2xl">
            <p class="text-xs font-extrabold uppercase tracking-[0.2em] text-[#2D8CFF]">Platforma Support</p>
            <h1 class="mt-3 text-3xl font-black tracking-tight text-slate-950 sm:text-4xl">How can we help?</h1>
            <p class="mt-3 text-sm leading-7 text-slate-600 sm:text-base">
              Send our team the details and we’ll reply to the email connected to your creator account.
            </p>
          </div>
          <div class="flex h-20 w-20 shrink-0 items-center justify-center rounded-3xl bg-slate-950 text-white shadow-xl shadow-slate-900/15">
            <ChatBubbleLeftRightIcon class="h-9 w-9" aria-hidden="true" />
          </div>
        </div>
      </div>
    </header>

    <section class="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_330px]">
      <form class="rounded-[26px] border border-slate-200 bg-white p-6 shadow-sm sm:p-8" @submit.prevent="submitSupportRequest">
        <div class="flex items-start justify-between gap-4">
          <div>
            <p class="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Contact the team</p>
            <h2 class="mt-2 text-xl font-extrabold text-slate-950">Create a support request</h2>
          </div>
          <span v-if="creatorEmail" class="hidden max-w-[220px] truncate rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600 sm:block">
            {{ creatorEmail }}
          </span>
        </div>

        <div v-if="successMessage" class="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-5" role="status">
          <div class="flex gap-3">
            <CheckCircleIcon class="mt-0.5 h-6 w-6 shrink-0 text-emerald-600" aria-hidden="true" />
            <div>
              <p class="font-bold text-emerald-900">Request sent</p>
              <p class="mt-1 text-sm leading-6 text-emerald-800">{{ successMessage }}</p>
              <p v-if="requestId" class="mt-2 text-xs font-semibold text-emerald-700">Reference: {{ requestId }}</p>
            </div>
          </div>
        </div>

        <div v-if="errorMessage" class="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700" role="alert">
          {{ errorMessage }}
        </div>

        <div class="mt-7 grid gap-5">
          <div>
            <label class="support-label" for="support-category">What do you need help with?</label>
            <PlatformaSelect
              id="support-category"
              v-model="form.category"
              class="mt-2"
              :options="categoryOptions"
              placeholder="Choose a category"
            />
          </div>

          <label>
            <span class="support-label">Subject</span>
            <input v-model.trim="form.subject" type="text" maxlength="190" required class="support-input mt-2" placeholder="A short summary of the issue">
          </label>

          <label>
            <span class="support-label">How can we help?</span>
            <textarea
              v-model.trim="form.message"
              rows="8"
              maxlength="5000"
              required
              class="support-input support-scroll mt-2 min-h-[190px] resize-y py-3"
              placeholder="Tell us what you were doing, what happened, and what you expected to happen."
            ></textarea>
            <span class="mt-2 block text-right text-xs font-medium text-slate-400">{{ form.message.length }} / 5000</span>
          </label>
        </div>

        <div class="mt-7 flex flex-col gap-3 border-t border-slate-100 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p class="text-xs leading-5 text-slate-500">Your account details are included automatically so we can help faster.</p>
          <button
            type="submit"
            :disabled="submitting || !canSubmit"
            class="inline-flex h-12 shrink-0 items-center justify-center gap-2 rounded-xl bg-[#2D8CFF] px-6 text-sm font-bold text-white shadow-lg shadow-blue-500/20 transition hover:bg-[#2379dc] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-200 disabled:cursor-not-allowed disabled:opacity-55"
          >
            <ArrowPathIcon v-if="submitting" class="h-4 w-4 animate-spin" aria-hidden="true" />
            <PaperAirplaneIcon v-else class="h-4 w-4" aria-hidden="true" />
            {{ submitting ? 'Sending request…' : 'Send Support Request' }}
          </button>
        </div>
      </form>

      <aside class="space-y-5">
        <a
          href="https://platformarea.com/help"
          target="_blank"
          rel="noopener noreferrer"
          class="group block overflow-hidden rounded-[24px] border border-slate-200 bg-slate-950 p-6 text-white shadow-sm transition hover:border-blue-400 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-200"
        >
          <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-blue-300">
            <BookOpenIcon class="h-6 w-6" aria-hidden="true" />
          </div>
          <p class="mt-6 text-xs font-bold uppercase tracking-[0.16em] text-blue-300">Help Docs</p>
          <h2 class="mt-2 text-xl font-extrabold">Find an answer now</h2>
          <p class="mt-2 text-sm leading-6 text-slate-300">Browse setup guides and step-by-step Platforma tutorials.</p>
          <span class="mt-5 inline-flex items-center gap-2 text-sm font-bold text-white">
            Browse Help Docs
            <ArrowTopRightOnSquareIcon class="h-4 w-4 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
          </span>
        </a>

        <div class="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm">
          <div class="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-[#2D8CFF]">
            <EnvelopeIcon class="h-5 w-5" aria-hidden="true" />
          </div>
          <p class="mt-5 text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Prefer email?</p>
          <p class="mt-2 break-all text-sm font-bold text-slate-900">support@platformarea.com</p>
          <button
            type="button"
            class="mt-4 inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-xs font-bold text-slate-700 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-100"
            @click="copySupportEmail"
          >
            <ClipboardDocumentIcon class="h-4 w-4" aria-hidden="true" />
            {{ emailCopied ? 'Email copied' : 'Copy email address' }}
          </button>
        </div>
      </aside>
    </section>
  </main>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
} from '@heroicons/vue/24/outline';
import PlatformaSelect from '@/Components/PlatformaSelect.vue';

const apiProxyUrl = String(process.env.VUE_APP_API_PROXY_URL || '').trim().replace(/\/+$/, '');
const creatorEmail = ref('');
const submitting = ref(false);
const successMessage = ref('');
const errorMessage = ref('');
const requestId = ref('');
const emailCopied = ref(false);
const form = reactive({ category: '', subject: '', message: '' });

const categoryOptions = [
  { value: 'account', label: 'Account & access' },
  { value: 'billing', label: 'Billing & plans' },
  { value: 'member-app', label: 'Member app & theme editor' },
  { value: 'products', label: 'Products & content' },
  { value: 'integrations', label: 'Integrations' },
  { value: 'technical', label: 'Technical issue' },
  { value: 'feedback', label: 'Product feedback' },
  { value: 'other', label: 'Something else' },
];

const canSubmit = computed(() => form.category && form.subject.trim().length >= 4 && form.message.trim().length >= 10);

const loadCreatorIdentity = async () => {
  try {
    const response = await fetch(`${apiProxyUrl}/api/creator-details`, { credentials: 'include', cache: 'no-store' });
    if (!response.ok) return;
    const payload = await response.json().catch(() => ({}));
    creatorEmail.value = String(payload?.email || payload?.creatorEmail || '').trim();
  } catch (_error) {
    creatorEmail.value = '';
  }
};

const submitSupportRequest = async () => {
  if (submitting.value || !canSubmit.value) return;
  submitting.value = true;
  successMessage.value = '';
  errorMessage.value = '';
  requestId.value = '';
  try {
    const response = await fetch(`${apiProxyUrl}/api/creator-support`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ category: form.category, subject: form.subject.trim(), message: form.message.trim() }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.success === false) throw new Error(payload?.message || 'Unable to send your support request.');
    successMessage.value = payload?.message || 'Your support request was sent. Our team will follow up by email.';
    requestId.value = String(payload?.requestId || '').trim();
    form.category = '';
    form.subject = '';
    form.message = '';
  } catch (error) {
    errorMessage.value = error?.message || 'Unable to send your support request. Please try again.';
  } finally {
    submitting.value = false;
  }
};

const copySupportEmail = async () => {
  try {
    await navigator.clipboard.writeText('support@platformarea.com');
    emailCopied.value = true;
    window.setTimeout(() => { emailCopied.value = false; }, 1800);
  } catch (_error) {
    emailCopied.value = false;
  }
};

onMounted(loadCreatorIdentity);
</script>

<style scoped>
.support-page { color: #18181b; }
.support-label { display: block; font-size: .75rem; font-weight: 800; letter-spacing: .04em; color: #334155; }
.support-input { width: 100%; min-height: 2.75rem; border: 1px solid #d4d4d8; border-radius: .75rem; background: #fff; padding-left: .875rem; padding-right: .875rem; font-size: .875rem; color: #18181b; outline: none; transition: border-color .15s ease, box-shadow .15s ease; }
.support-input::placeholder { color: #94a3b8; }
.support-input:hover { border-color: #a1a1aa; }
.support-input:focus { border-color: #2d8cff; box-shadow: 0 0 0 3px rgba(45,140,255,.13); }
.support-scroll { scrollbar-width: thin; scrollbar-color: #cbd5e1 transparent; }
.support-scroll::-webkit-scrollbar { width: 7px; }
.support-scroll::-webkit-scrollbar-track { background: transparent; }
.support-scroll::-webkit-scrollbar-thumb { border-radius: 999px; background: #cbd5e1; }
.support-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
</style>
